import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import { useAuthUser } from '../../hooks/useAuthUser';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import style from './OnboardingFlow.module.css';

const API_URL = process.env.REACT_APP_API_URL;

const OnboardingPaymentSuccess = () => {
	const { user, isLoaded } = useAuthUser();
	const location = useLocation();
	const navigate = useNavigate();
	const [isVerifying, setIsVerifying] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');

	const routineId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return params.get('routineId') || '';
	}, [location.search]);

	useEffect(() => {
		if (!isLoaded) return;

		if (!routineId) {
			setErrorMessage('Missing routine reference from checkout.');
			setIsVerifying(false);
			return;
		}

		if (!user?.id) {
			navigate(`/login?redirectTo=${encodeURIComponent(`/onboarding/payment-success?routineId=${routineId}`)}`, { replace: true });
			return;
		}

		const verifyAndActivate = async () => {
			setIsVerifying(true);
			setErrorMessage('');
			try {
				const response = await requestWithApiFallback(API_URL, (base) => axios.post(
					`${base}/onboarding/activate/${encodePathSegment(user.id)}/${encodePathSegment(routineId)}`,
					{},
					{ withCredentials: true }
				));
				const redirectRoute = response.data?.route || `/routine/${routineId}`;
				navigate(redirectRoute, { replace: true });
			} catch (error) {
				console.error('Failed to activate onboarding routine after payment:', error);
				setErrorMessage(error?.response?.data?.error || 'Payment is not fully confirmed yet. Please retry in a moment.');
				setIsVerifying(false);
			}
		};

		verifyAndActivate();
	}, [isLoaded, navigate, routineId, user?.id]);

	if (isVerifying) {
		return (
			<div className={style.LoadingWrap}>
				<p>Verifying payment and unlocking your routine...</p>
			</div>
		);
	}

	return (
		<div className={style.OnboardingPage}>
			<SEO
				title="Finalizing your plan"
				description="Finalizing your payment and loading your routine."
				path="/onboarding/payment-success"
				noIndex
			/>
			<div className={style.OnboardingCard}>
				<p className={style.MutedText}>{errorMessage}</p>
				<div className={style.CTAStack}>
					<PrimaryButton
						text="Retry unlock"
						click={() => window.location.reload()}
						width="100%"
					/>
					<SecondaryButton
						text="Back to plans"
						click={() => navigate(`/upgrade?source=onboarding&routineId=${encodeURIComponent(routineId)}`)}
						width="100%"
					/>
				</div>
			</div>
		</div>
	);
};

export default OnboardingPaymentSuccess;
